import Dexie from "dexie";

const db = new Dexie("rms_pos_offline");

db.version(1).stores({
  orders: "++id, status, createdAt, retryCount",
  products: "categoryKey, cachedAt",
  categories: "cacheId, cachedAt",
});
// shop customers kept for phone and name search without internet (key = last 10 digits)
db.version(2).stores({
  orders: "++id, status, createdAt, retryCount",
  products: "categoryKey, cachedAt",
  categories: "cacheId, cachedAt",
  customers: "key",
});

// the state of the customer update, saved in the same step as the customers
db.version(3).stores({
  orders: "++id, status, createdAt, retryCount",
  products: "categoryKey, cachedAt",
  categories: "cacheId, cachedAt",
  customers: "key",
  meta: "key",
});

export const offlineDb = db;

//  Helpers

function generateLocalId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 7).toUpperCase()}`;
}

function isNetworkError(error) {
  return (
    !navigator.onLine ||
    error?.code === "ERR_NETWORK" ||
    error?.message?.toLowerCase().includes("network") ||
    error?.message?.toLowerCase().includes("failed to fetch") ||
    error?.response === undefined
  );
}

//  ORDERS

async function enqueueOrder(payload, meta = {}) {
  const localId = generateLocalId();
  const dbId = await db.orders.add({
    localId,
    payload,
    productsSnapshot: meta.productsSnapshot || [],
    pricing: meta.pricing || null,
    delivery: meta.delivery || null,
    status: "pending",
    createdAt: Date.now(),
    syncedAt: null,
    serverId: null,
    retryCount: 0,
    error: null,
  });
  return { dbId, localId };
}

async function listAllOrders() {
  const all = await db.orders.orderBy("createdAt").reverse().toArray();
  return all;
}

async function listPendingOrders() {
  return db.orders
    .where("status")
    .anyOf(["pending", "failed"])
    .filter((o) => o.retryCount < 3)
    .toArray();
}

async function markOrderSyncing(id) {
  await db.orders.update(id, { status: "syncing" });
}

async function markOrderSynced(id, serverId) {
  await db.orders.update(id, {
    status: "synced",
    serverId,
    syncedAt: Date.now(),
    error: null,
  });
}

async function markOrderFailed(id, errorMsg) {
  const record = await db.orders.get(id);
  if (!record) return;
  const newRetryCount = (record.retryCount || 0) + 1;
  const newStatus = newRetryCount >= 3 ? "permanently_failed" : "failed";
  await db.orders.update(id, {
    status: newStatus,
    retryCount: newRetryCount,
    error: errorMsg,
  });
}

// no answer from the server: waits for the next try, not counted as a failure
async function markOrderPending(id) {
  await db.orders.update(id, { status: "pending" });
}

// sending stopped half way (page closed or reloaded): send again; the server
// knows each sale by its id, so it is never saved twice
async function resetStuckSyncing() {
  await db.orders
    .where("status")
    .equals("syncing")
    .modify({ status: "pending" });
}

async function resetOrderForRetry(id) {
  await db.orders.update(id, {
    status: "pending",
    retryCount: 0,
    error: null,
  });
}

async function deleteOrder(id) {
  await db.orders.delete(id);
}

async function clearSyncedOrders() {
  const syncedOrders = await db.orders
    .where("status")
    .equals("synced")
    .toArray();
  const count = syncedOrders.length;
  await db.orders.where("status").equals("synced").delete();
  return count;
}

//  PRODUCTS

async function cacheProducts(categoryKey, list, total) {
  await db.products.put({ categoryKey, list, total, cachedAt: Date.now() });
}

async function getCachedProducts(categoryKey) {
  const record = await db.products.get(categoryKey);
  return record || null;
}

async function getAllCachedProducts() {
  try {
    const records = await db.products.toArray();
    const map = {};
    records.forEach((rec) => {
      (rec.list || []).forEach((p) => {
        if (p && p.id) {
          map[p.id] = p;
        }
      });
    });
    return map;
  } catch (err) {
    console.error("Failed to load cached products:", err);
    return {};
  }
}

//  CATEGORIES

const CATEGORY_CACHE_ID = "main";

async function cacheCategories(list) {
  await db.categories.put({
    cacheId: CATEGORY_CACHE_ID,
    list,
    cachedAt: Date.now(),
  });
}

async function getCachedCategories() {
  const record = await db.categories.get(CATEGORY_CACHE_ID);
  return record || null;
}

export const OfflineOrderServices = {
  enqueueOrder,
  listAllOrders,
  listPendingOrders,
  markOrderSyncing,
  markOrderSynced,
  markOrderFailed,
  markOrderPending,
  resetStuckSyncing,
  resetOrderForRetry,
  deleteOrder,
  clearSyncedOrders,
  cacheProducts,
  getCachedProducts,
  getAllCachedProducts,
  cacheCategories,
  getCachedCategories,
  isNetworkError,
};
